import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui';
import { reportClientError } from '../../utils/errorReporting';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    
    reportClientError({
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo, onReset }) => {
  const { colors } = useTheme();
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <div className={cn('flex items-center justify-center min-h-screen p-6', colors.background)}>
      <div className={cn('max-w-2xl w-full rounded-lg shadow-lg border p-8', colors.surface, colors.border)}>
        <div className="text-center mb-6">
          <div className={cn('mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4', colors.error)}>
            <svg
              className="w-8 h-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className={cn('text-2xl font-bold mb-2', colors.text)}>Something went wrong</h2>
          <p className={cn(colors.textSecondary, 'mb-6')}>
            An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
          </p>
        </div>

        {isDevelopment && error && (
          <div className={cn('mb-6 p-4 rounded-lg border', colors.error, colors.border)}>
            <h3 className="text-sm font-semibold mb-2">Error Details (Development Only):</h3>
            <pre className="text-xs whitespace-pre-wrap overflow-auto max-h-64 opacity-90">
              {error.toString()}
              {errorInfo?.componentStack && `\n\nComponent Stack:\n${errorInfo.componentStack}`}
            </pre>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <Button onClick={onReset} variant="default">
            Go to Home
          </Button>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;

